/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Page: Clubs
 * File     : Clubs.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   React page component rendering the Clubs view and coordinating data fetching.
 *   Exports: Clubs
 *   
 *
 * Dependencies:
 *   - ../context/AuthContext
 *   - ../services/api
 *   - @mui/icons-material/ContentCopy
 *   - @mui/icons-material/Lock
 *   - @mui/material
 *   - react
 *   - react-router-dom
 *
 * Usage:
 *   Use in Router: <Route path="/clubs" element={<Clubs />} />
 * -----------------------------------------------------------------------------
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Stack, TextField, Button, List, ListItemButton, ListItemText, IconButton, Tooltip, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { createClub, listClubs, getClubInvite, joinClub, leaveClub } from '../services/api';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const Clubs = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [name, setName] = useState('');
  const [invite, setInvite] = useState({});
  const [joinCode, setJoinCode] = useState('');
  const [limitOpen, setLimitOpen] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');
  const [subscribeLoading, setSubscribeLoading] = useState(false);

  const requiresHostPlan = !!user && user.role !== 'owner' && user.planStatus !== 'active';

  useEffect(() => {
    if (!user) return;
    listClubs().then(r => setClubs(r.clubs || [])).catch(() => setClubs([]));
  }, [user]);

  const goSubscribe = async () => {
    setSubscribeError('');
    setSubscribeLoading(true);
    try {
      const r = await api.post('/billing/checkout', {});
      if (r?.data?.url) {
        window.location.href = r.data.url;
        return;
      }
      setSubscribeError('Checkout could not be started. Please try again.');
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Could not start checkout. Please try again.';
      setSubscribeError(msg);
    } finally {
      setSubscribeLoading(false);
    }
  };

  const onCreate = async () => {
    if (!name.trim()) return;
    try {
      await createClub(name.trim());
    } catch (e) {
      if (e?.response?.status === 402) {
        const r = await api.post('/billing/checkout');
        if (r?.data?.url) window.location.href = r.data.url;
        return;
      } else if (e?.response?.status === 429 || e?.response?.data?.error?.toLowerCase?.().includes('max') || e?.response?.data?.error?.toLowerCase?.().includes('limit')) {
        setLimitOpen(true);
        return;
      }
      throw e;
    }
    setName('');
    const r = await listClubs();
    setClubs(r.clubs || []);
  };

  const loadInvite = async (id) => {
    try {
      const r = await getClubInvite(id);
      setInvite(prev => ({ ...prev, [id]: r.inviteCode }));
      await navigator.clipboard.writeText(r.inviteCode);
    } catch (_) {}
  };

  const onJoin = async () => {
    if (!joinCode.trim()) return;
    try {
      await joinClub(joinCode.trim());
      setJoinCode('');
      const r = await listClubs();
      setClubs(r.clubs || []);
    } catch (_) {}
  };

  if (!user) return <Typography>Sign in to manage clubs</Typography>;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Clubs</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField size="small" label="New club name" value={name} onChange={e => setName(e.target.value)} disabled={requiresHostPlan} />
          <Button variant="contained" onClick={onCreate} disabled={requiresHostPlan}>Create Club</Button>
          <Box flex={1} />
          <TextField size="small" label="Join with invite" value={joinCode} onChange={e => setJoinCode(e.target.value)} />
          <Button onClick={onJoin}>Join</Button>
        </Stack>
        {requiresHostPlan && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <Chip color="warning" size="small" icon={<LockIcon fontSize="small" />} label="Host plan required to create clubs" />
            <Button size="small" onClick={goSubscribe} disabled={subscribeLoading}>
              {subscribeLoading ? 'Loading…' : 'Subscribe'}
            </Button>
          </Stack>
        )}
        {subscribeError && (
          <Alert severity="error" onClose={() => setSubscribeError('')} sx={{ mt: 1 }}>
            {subscribeError}
          </Alert>
        )}
      </Paper>

      <Dialog open={limitOpen} onClose={() => setLimitOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Club Limit Reached</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            You’ve reached the maximum number of clubs for your subscription.
          </Typography>
          <Typography variant="body2">
            Remove a club before trying again, or purchase extra slots to continue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitOpen(false)}>Close</Button>
          <Button variant="contained" onClick={() => { setLimitOpen(false); navigate('/pricing'); }}>Purchase Slots</Button>
        </DialogActions>
      </Dialog>

      <Paper>
        <List>
          {clubs.filter(c => c && c.id).map(c => (
            <ListItemButton key={c.id} divider onClick={() => navigate(`/clubs/${c.id}`)}>
              <ListItemText primary={c.name} secondary={invite[c.id] ? `Invite: ${invite[c.id]}` : `Private Club created by ${c.owner?.username || 'Unknown'}`} />
              <Box display="flex" gap={1}>
                <Tooltip title="Club Streamer">
                  <IconButton 
                    edge="end" 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      navigate(`/clubs/${c.id}/streamer`); 
                    }}
                    color="primary"
                  >
                    <PlayArrowIcon />
                  </IconButton>
                </Tooltip>
                {(user?.role === 'owner' || user?.role === 'admin' || user?.id === c.owner?.id) ? (
                  <Tooltip title="Copy invite code">
                    <IconButton edge="end" onClick={(e) => { e.stopPropagation(); loadInvite(c.id); }}>
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Button size="small" onClick={async (e) => { e.stopPropagation(); await leaveClub(c.id); const r = await listClubs(); setClubs(r.clubs || []); }}>Leave</Button>
                )}
              </Box>
            </ListItemButton>
          ))}
        </List>
      </Paper>
    </Box>
  );
};

export default Clubs;


